import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Target, Heart, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export default function About() {
  const imageRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"]
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 md:py-28 border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              The Story
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Why I created The Signal
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-[#999999] max-w-2xl">
              A system born from personal transformation and the belief that every man deserves to look and feel his best.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center"
          >
            {/* Video with creative styling */}
            <motion.div variants={fadeInUp} className="relative group">
              {/* Glow effect behind video */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/20 via-transparent to-[#D4AF37]/10 blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
              
              {/* Video container with rounded corners and border */}
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#2a2a2a] shadow-2xl shadow-black/50">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  style={{ filter: 'brightness(0.65) contrast(1.1) saturate(1.1)' }}
                  onLoadedData={(e) => e.target.play()}
                >
                  <source src={`${process.env.REACT_APP_BACKEND_URL}/api/static/about-video.mp4`} type="video/mp4" />
                </video>
                
                {/* Subtle vignette overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
                
                {/* TikTok button - centered */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
                  <a 
                    href="https://www.tiktok.com/@rc.vnl" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-6 py-3 rounded-full font-semibold hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    @rc.vnl
                  </a>
                  <p className="text-white/70 text-sm mt-3 tracking-wide">Follow the journey</p>
                </div>
                
                {/* Corner accents */}
                <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#D4AF37]/50 rounded-tl-lg" />
                <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#D4AF37]/50 rounded-br-lg" />
              </div>
            </motion.div>
            
            <motion.div variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white mb-6">
                From struggling to systematic
              </motion.h2>
              <motion.div variants={staggerContainer} className="space-y-6 text-[#999999] leading-relaxed">
                <motion.p variants={fadeInUp}>
                  Like many men, I spent years feeling like something was off. Inconsistent skin, no real style, 
                  unsure how to present myself. I knew appearance mattered, but I didn't have a system.
                </motion.p>
                <motion.p variants={fadeInUp}>
                  So I built one. I studied dermatology, fitness science, style theory, body language, 
                  and personal development. I tested everything on myself first.
                </motion.p>
                <motion.p variants={fadeInUp}>
                  The transformation wasn't overnight. It took months of consistent effort. But the results 
                  were undeniable—not just in how I looked, but in how people responded to me.
                </motion.p>
                <motion.p variants={fadeInUp} className="font-medium text-white">
                  The Signal is everything I wish I had when I started. A complete, structured system 
                  for men who understand that your appearance shapes your opportunities.
                </motion.p>
              </motion.div>
              <motion.div variants={fadeInUp} className="w-16 h-[1px] bg-[#D4AF37] my-8" />
              <motion.p variants={fadeInUp} className="font-serif text-xl text-white italic">
                "Your appearance is the first thing people see. Make it count."
              </motion.p>
              <motion.p variants={fadeInUp} className="text-sm text-[#D4AF37] mt-2">— Rares</motion.p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 md:py-28 bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              Philosophy
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white mb-6">
              What we believe
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              { 
                icon: Target, 
                title: "Intentional", 
                desc: "Nothing is left to chance. Every habit, every routine, every choice is deliberate." 
              },
              { 
                icon: Zap, 
                title: "Efficient", 
                desc: "No wasted time on what doesn't work. Only proven methods that deliver results." 
              },
              { 
                icon: Heart, 
                title: "Sustainable", 
                desc: "Habits that fit into your life. Systems you can maintain for years, not weeks." 
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="border border-[#333333] p-8 text-center"
              >
                <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-xl text-white mb-3">{item.title}</h3>
                <p className="text-[#999999] text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-4xl mx-auto px-6 md:px-12 text-center"
        >
          <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white mb-6">
            Ready to transform?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#999999] mb-8">
            Join the men who have taken control of their appearance and their lives.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/products">
              <Button 
                className="btn-premium bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none px-10 py-6 text-sm uppercase tracking-widest font-semibold"
                data-testid="view-products-cta"
              >
                View Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
